(function () {
  'use strict';

  if (customElements.get('product-sticky-mobile')) return;

  const OVERLAY_SELECTOR = [
    'dialog[open]',
    '[aria-modal="true"]',
    '[role="alertdialog"]',
    'cart-drawer.is-open',
    'drawer-element.is-open',
    '.pswp--open',
  ].join(',');

  class ProductStickyMobile extends HTMLElement {
    constructor() {
      super();

      this.armed = false;
      this.passedButton = false;
      this.footerInView = false;
      this.footerIntersecting = false;
      this.previousScrollY = window.scrollY;
      this.frame = null;
      this.visible = false;
      this.userScrollIntent = false;
      this.variantPending = false;
      this.variantRequestTimer = null;

      this.handleScroll = this.handleScroll.bind(this);
      this.handleResize = this.handleResize.bind(this);
      this.handleSubmit = this.handleSubmit.bind(this);
      this.handleOverlayChange = this.handleOverlayChange.bind(this);
      this.handleVariantChange = this.handleVariantChange.bind(this);
      this.handleVariantSelectChange = this.handleVariantSelectChange.bind(this);
      this.handleUserScrollIntent = this.handleUserScrollIntent.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
    }

    connectedCallback() {
      this.stickyButton = this.querySelector('[data-sticky-add-to-cart]');
      this.stickyButtonText = this.querySelector('[data-sticky-add-to-cart-text]');
      this.price = this.querySelector('[data-sticky-product-price]');
      this.productImage = this.querySelector('[data-sticky-product-image]');
      this.variantSelect = this.querySelector('[data-sticky-variant-select]');

      this.refreshMainForm();
      this.syncFromMainForm();

      if (this.mainButton) {
        this.armed = this.mainButton.getBoundingClientRect().bottom > 0;
      }

      this.stickyButton?.addEventListener('click', this.handleSubmit);
      this.variantSelect?.addEventListener('change', this.handleVariantSelectChange);
      document.addEventListener('theme:scroll', this.handleScroll);
      document.addEventListener('keydown', this.handleKeydown);
      window.addEventListener('theme:resize:width', this.handleResize);
      window.addEventListener('touchstart', this.handleUserScrollIntent, {passive: true});
      window.addEventListener('wheel', this.handleUserScrollIntent, {passive: true});

      ['theme:cart:open', 'theme:cart:close', 'theme:drawer:open', 'theme:drawer:close', 'theme:scroll:lock', 'theme:scroll:unlock'].forEach(
        (eventName) => document.addEventListener(eventName, this.handleOverlayChange)
      );

      if (window.subscribe && window.theme?.PUB_SUB_EVENTS?.variantChange) {
        this.variantChangeUnsubscriber = window.subscribe(
          window.theme.PUB_SUB_EVENTS.variantChange,
          this.handleVariantChange
        );
      }

      this.observeFooter();
      this.observePageChanges();
      this.scheduleUpdate(false);
    }

    disconnectedCallback() {
      this.stickyButton?.removeEventListener('click', this.handleSubmit);
      this.variantSelect?.removeEventListener('change', this.handleVariantSelectChange);
      document.removeEventListener('theme:scroll', this.handleScroll);
      document.removeEventListener('keydown', this.handleKeydown);
      window.removeEventListener('theme:resize:width', this.handleResize);
      window.removeEventListener('touchstart', this.handleUserScrollIntent);
      window.removeEventListener('wheel', this.handleUserScrollIntent);

      ['theme:cart:open', 'theme:cart:close', 'theme:drawer:open', 'theme:drawer:close', 'theme:scroll:lock', 'theme:scroll:unlock'].forEach(
        (eventName) => document.removeEventListener(eventName, this.handleOverlayChange)
      );

      this.variantChangeUnsubscriber?.();
      this.footerObserver?.disconnect();
      this.pageObserver?.disconnect();
      if (this.variantRequestTimer !== null) window.clearTimeout(this.variantRequestTimer);

      if (this.frame !== null) cancelAnimationFrame(this.frame);
    }

    refreshMainForm() {
      const productInfo = this.closest('[data-section-type="product-template"]')?.querySelector('product-info');
      if (!productInfo) return;

      this.productInfo = productInfo;
      this.productForm = productInfo.querySelector('product-form[data-form-wrapper]');
      this.mainForm = this.productForm?.querySelector('form[data-product-form]');
      this.mainButton = this.productForm?.querySelector('[data-add-to-cart]');
    }

    handleScroll() {
      this.scheduleUpdate(true);
    }

    handleUserScrollIntent() {
      this.userScrollIntent = true;
    }

    handleKeydown(event) {
      if (['ArrowDown', 'End', 'PageDown', ' '].includes(event.key)) {
        this.handleUserScrollIntent();
      }
    }

    handleResize() {
      this.refreshMainForm();
      this.previousScrollY = window.scrollY;
      this.passedButton = false;
      this.armed = Boolean(this.mainButton && this.mainButton.getBoundingClientRect().bottom > 0);

      this.scheduleUpdate(false);
    }

    handleOverlayChange() {
      this.scheduleUpdate(false);
    }

    handleVariantChange(event) {
      if (event?.data?.sectionId && event.data.sectionId !== this.productInfo?.dataset.section) return;

      const variant = event?.data?.variant;
      if (variant) {
        this.setVariantPending(false);
        this.updateVariantDetails(variant);
      }

      requestAnimationFrame(() => {
        if (!this.isConnected) return;
        this.refreshMainForm();
        this.syncFromMainForm();
        this.scheduleUpdate(false);
      });
    }

    handleSubmit() {
      this.refreshMainForm();
      this.syncFromMainForm();

      if (!this.mainForm || !this.mainButton || this.stickyButton.disabled || this.hasActiveOverlay()) return;

      this.mainForm.requestSubmit(this.mainButton);
    }

    handleVariantSelectChange(event) {
      const selectedOption = event.target.selectedOptions?.[0];
      if (!selectedOption || !this.productInfo) return;

      const optionValues = [1, 2, 3]
        .map((position) => selectedOption.getAttribute(`data-option-${position}`))
        .filter((value) => value);
      const variantSelectors = this.productInfo.querySelector('variant-selects');
      if (!variantSelectors || optionValues.length === 0) return;

      const updates = optionValues.map((value, index) => {
        const wrapper = variantSelectors.querySelector(`[data-option-position="${index + 1}"]`);
        if (!wrapper) return null;

        const radio = Array.from(wrapper.querySelectorAll('input[type="radio"][data-single-option-selector]')).find(
          (input) => input.value === value
        );
        if (radio) return {input: radio, type: 'radio'};

        const input = wrapper.querySelector('[data-popout-input][data-single-option-selector]');
        const valueItem = Array.from(wrapper.querySelectorAll('[data-option-value-id]')).find((item) => {
          const option = item.querySelector?.('[data-popout-option]');
          return (option?.dataset.value || item.getAttribute('value')) === value;
        });
        if (!input || !valueItem) return null;

        return {
          input,
          type: 'popout',
          value,
          optionValueId: valueItem.dataset.optionValueId,
          productUrl: valueItem.dataset.productUrl || '',
        };
      });

      if (updates.some((update) => update === null)) {
        this.syncSelectedVariantFromMainForm();
        return;
      }

      updates.forEach((update) => {
        if (update.type === 'radio') {
          update.input.checked = true;
          return;
        }

        update.input.value = update.value;
        update.input.dataset.optionValueId = update.optionValueId;
        update.input.dataset.productUrl = update.productUrl;
      });

      this.setVariantPending(true);
      updates[updates.length - 1].input.dispatchEvent(new Event('change', {bubbles: true}));
    }

    setVariantPending(pending) {
      this.variantPending = pending;
      if (this.variantRequestTimer !== null) {
        window.clearTimeout(this.variantRequestTimer);
        this.variantRequestTimer = null;
      }

      if (pending) {
        this.variantRequestTimer = window.setTimeout(() => {
          this.variantRequestTimer = null;
          this.variantPending = false;
          this.refreshMainForm();
          this.syncFromMainForm();
        }, 8000);
      }

      this.toggleAttribute('aria-busy', pending);
      this.syncFromMainForm();
    }

    updateVariantDetails(variant) {
      const format = window.theme.settings.currency_code_enable
        ? window.theme.moneyWithCurrencyFormat
        : window.theme.moneyFormat;
      const price = variant.price === 0 ? window.theme.strings.free : window.theme.formatMoney(variant.price, format);
      const title = variant.title === 'Default Title' ? '' : variant.title;

      if (this.price) this.price.textContent = price;
      if (this.variantSelect && title) {
        const option = Array.from(this.variantSelect.options).find((item) => item.value === String(variant.id));
        if (option) this.variantSelect.value = option.value;
      }
      this.updateProductImage();
    }

    syncFromMainForm() {
      if (!this.mainButton || !this.stickyButton) return;

      const sourceText = this.mainButton.querySelector('[data-add-to-cart-text]');
      if (sourceText && this.stickyButtonText) {
        const textCopy = sourceText.cloneNode(true);
        textCopy.querySelectorAll('[data-product-price], [data-compare-price]').forEach((element) => element.remove());
        const label = textCopy.textContent.replace(/\s+/g, ' ').trim();
        if (label && this.stickyButtonText.textContent !== label) this.stickyButtonText.textContent = label;
      }

      if (!this.variantPending) this.syncSelectedVariantFromMainForm();

      const isLoading = this.variantPending || this.mainButton.classList.contains('is-loading');
      const isDisabled = this.variantPending || this.mainButton.disabled;
      if (this.stickyButton.disabled !== isDisabled) {
        this.stickyButton.disabled = isDisabled;
      }

      this.stickyButton.classList.toggle('is-loading', isLoading);
      this.stickyButton.classList.toggle('is-success', !this.variantPending && this.mainButton.classList.contains('is-success'));
      if (this.variantSelect) this.variantSelect.disabled = isLoading;

      const ariaLabel = this.stickyButtonText.textContent.trim();
      if (this.stickyButton.getAttribute('aria-label') !== ariaLabel) {
        this.stickyButton.setAttribute('aria-label', ariaLabel);
      }
    }

    syncSelectedVariantFromMainForm() {
      if (!this.variantSelect || !this.mainForm) return;

      const variantId = this.mainForm.querySelector('input[name="id"]')?.value;
      const option = Array.from(this.variantSelect.options).find((item) => item.value === String(variantId));
      if (!option) return;

      this.variantSelect.value = option.value;
      this.updateProductImage();
    }

    updateProductImage() {
      if (!this.productImage || !this.variantSelect) return;

      const imageUrl = this.variantSelect.selectedOptions?.[0]?.dataset.imageUrl;
      if (!imageUrl || this.productImage.getAttribute('src') === imageUrl) return;

      this.productImage.srcset = imageUrl;
      this.productImage.src = imageUrl;
    }

    observeFooter() {
      const footer = document.querySelector('[data-section-type="footer"]');
      if (!footer || !('IntersectionObserver' in window)) return;

      this.footerElement = footer;
      this.footerObserver = new IntersectionObserver(([entry]) => {
        this.footerIntersecting = entry.isIntersecting;
        this.scheduleUpdate(false);
      });
      this.footerObserver.observe(footer);
    }

    observePageChanges() {
      if (!document.body || !('MutationObserver' in window)) return;

      this.pageObserver = new MutationObserver((mutations) => {
        const relevant = mutations.some((mutation) => this.isPageMutationRelevant(mutation));
        if (!relevant) return;

        this.refreshMainForm();
        this.syncFromMainForm();
        this.scheduleUpdate(false);
      });
      this.pageObserver.observe(document.body, {
        attributes: true,
        attributeFilter: ['aria-hidden', 'aria-modal', 'class', 'disabled', 'open', 'style'],
        childList: true,
        subtree: true,
      });
    }

    isPageMutationRelevant(mutation) {
      const target = mutation.target.nodeType === Node.ELEMENT_NODE ? mutation.target : mutation.target.parentElement;
      if (!target || target === this || this.contains(target)) return false;
      if (target === document.body || this.productInfo?.contains(target)) return true;

      const changedOverlay = [...mutation.addedNodes, ...mutation.removedNodes].some((node) => {
        if (node.nodeType !== Node.ELEMENT_NODE) return false;

        return node.matches?.(OVERLAY_SELECTOR) || node.querySelector?.(OVERLAY_SELECTOR);
      });

      return Boolean(
        changedOverlay ||
          target.matches?.(OVERLAY_SELECTOR) ||
          target.closest?.(OVERLAY_SELECTOR) ||
          target.querySelector?.(OVERLAY_SELECTOR)
      );
    }

    scheduleUpdate(fromScroll) {
      this.updateFromScroll = this.updateFromScroll || fromScroll;
      if (this.frame !== null) return;

      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        const shouldUpdatePosition = this.updateFromScroll;
        this.updateFromScroll = false;
        this.updateVisibility(shouldUpdatePosition);
      });
    }

    updateVisibility(fromScroll) {
      const currentScrollY = window.scrollY;
      const captchaBadge = document.querySelector('#shop-hcaptcha-badge-container');

      this.footerInView = Boolean(
        this.footerElement && this.footerIntersecting && this.isVisibleOverlay(this.footerElement)
      );

      this.classList.toggle(
        'has-captcha-obstruction',
        Boolean(captchaBadge && this.isVisibleOverlay(captchaBadge))
      );

      if (this.mainButton) {
        const buttonBottom = this.mainButton.getBoundingClientRect().bottom;
        const scrollingDown = currentScrollY > this.previousScrollY;

        if (buttonBottom > 0) {
          this.armed = true;
          this.passedButton = false;
        } else if (fromScroll && !this.userScrollIntent) {
          this.armed = false;
          this.passedButton = false;
        } else if (fromScroll && this.armed && scrollingDown) {
          this.passedButton = true;
        }
      }

      if (fromScroll) this.previousScrollY = currentScrollY;

      const visible =
        Boolean(this.mainButton) &&
        this.passedButton &&
        !this.footerInView &&
        !this.hasActiveOverlay();

      this.setVisible(visible);
    }

    setVisible(visible) {
      if (visible === this.visible) return;
      this.visible = visible;

      if (!visible && document.activeElement === this.stickyButton) this.stickyButton.blur();

      this.classList.toggle('is-visible', visible);
      this.setAttribute('aria-hidden', String(!visible));
      this.stickyButton?.setAttribute('tabindex', visible ? '0' : '-1');
    }

    hasActiveOverlay() {
      const root = document.documentElement;
      const body = document.body;

      if (root.hasAttribute('data-scroll-locked')) return true;
      if (body.classList.contains('js-drawer-open') || body.classList.contains('js-drawer-open-cart')) return true;

      return Array.from(document.querySelectorAll(OVERLAY_SELECTOR)).some((element) => this.isVisibleOverlay(element));
    }

    isVisibleOverlay(element) {
      if (element.getAttribute('aria-hidden') === 'true' || element.closest('details:not([open])')) return false;
      if (element.checkVisibility?.({checkOpacity: true, checkVisibilityCSS: true}) === false) return false;

      let current = element;
      while (current) {
        const style = window.getComputedStyle(current);
        if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) return false;
        if (current === document.body) break;
        current = current.parentElement;
      }

      const rect = element.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.top < window.innerHeight && rect.right > 0 && rect.left < window.innerWidth;
    }
  }

  customElements.define('product-sticky-mobile', ProductStickyMobile);
})();
